import React, { useState, useEffect } from 'react';
import { supabase } from '../database/supabase';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import './TeacherEvaluationAdmin.css';

// Supabase caps queries at 1000 rows by default. This helper paginates
// through all pages until every row is fetched.
async function fetchAllRows(buildQuery) {
  const PAGE_SIZE = 1000;
  let allData = [];
  let from = 0;
  while (true) {
    const { data, error } = await buildQuery().range(from, from + PAGE_SIZE - 1);
    if (error || !data || data.length === 0) break;
    allData = allData.concat(data);
    if (data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return allData;
}

export default function TeacherEvaluationAdmin() {
  const [activeTab, setActiveTab] = useState('forms');
  const [loading, setLoading] = useState(false);
  const [assignmentView, setAssignmentView] = useState('teacher-subject');
  const [assignmentStatusFilter, setAssignmentStatusFilter] = useState('');

  return (
    <div className="teacher-eval-admin">
      <h1>Teacher Evaluation Management</h1>
      
      <div className="tabs">
        <button className={activeTab === 'forms' ? 'active' : ''} onClick={() => setActiveTab('forms')}>
          Evaluation Forms
        </button>
        <button className={activeTab === 'teachers' ? 'active' : ''} onClick={() => setActiveTab('teachers')}>
          Teachers
        </button>
        <button className={activeTab === 'subjects' ? 'active' : ''} onClick={() => setActiveTab('subjects')}>
          Subjects
        </button>
        <button className={activeTab === 'students' ? 'active' : ''} onClick={() => setActiveTab('students')}>
          Student Accounts
        </button>
        <button className={activeTab === 'assignments' ? 'active' : ''} onClick={() => setActiveTab('assignments')}>
          Assignments
        </button>
        <button className={activeTab === 'submissions' ? 'active' : ''} onClick={() => setActiveTab('submissions')}>
          Submissions
        </button>
        <button className={activeTab === 'analytics' ? 'active' : ''} onClick={() => setActiveTab('analytics')}>
          Analytics
        </button>
        <button className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>
          Overview
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'forms' && <EvaluationFormsTab />}
        {activeTab === 'teachers' && <TeachersTab />}
        {activeTab === 'subjects' && <SubjectsTab />}
        {activeTab === 'students' && <StudentAccountsTab />}
        {activeTab === 'assignments' && <AssignmentsTab initialView={assignmentView} initialStatusFilter={assignmentStatusFilter} />}
        {activeTab === 'submissions' && <SubmissionsTab />}
        {activeTab === 'analytics' && <AnalyticsTab />}
        {activeTab === 'overview' && <OverviewTab setActiveTab={setActiveTab} setAssignmentView={setAssignmentView} setAssignmentStatusFilter={setAssignmentStatusFilter} />}
      </div>
    </div>
  );
}

function EvaluationFormsTab() {
  const [forms, setForms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingForm, setEditingForm] = useState(null);

  useEffect(() => {
    loadForms();
  }, []);

  const loadForms = async () => {
    const { data } = await supabase.from('evaluation_forms').select('*').order('created_at', { ascending: false });
    setForms(data || []);
  };

  const toggleActive = async (id, isActive) => {
    await supabase.from('evaluation_forms').update({ is_active: !isActive }).eq('id', id);
    loadForms();
  };

  const deleteForm = async (id) => {
    if (window.confirm('Delete this form?')) {
      await supabase.from('evaluation_forms').delete().eq('id', id);
      loadForms();
    }
  };

  const duplicateForm = async (form) => {
    if (window.confirm(`Duplicate "${form.title}"?`)) {
      const newForm = {
        title: `${form.title} (Copy)`,
        description: form.description,
        fields: form.fields,
        is_active: false
      };
      const { error } = await supabase.from('evaluation_forms').insert([newForm]);
      if (error) {
        toast.error('Failed to duplicate form');
      } else {
        toast.success('Form duplicated successfully');
        loadForms();
      }
    }
  };

  return (
    <div>
      <button className="btn-primary" onClick={() => { setEditingForm(null); setShowModal(true); }}>
        Create Evaluation Form
      </button>

      <table>
        <thead>
          <tr>
            <th>Title</th>
            <th>Questions</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {forms.map(form => (
            <tr key={form.id}>
              <td>{form.title}</td>
              <td>{form.fields?.sections?.reduce((sum, s) => sum + (s.fields?.length || 0), 0) || 0}</td>
              <td>
                <span className={`status ${form.is_active ? 'active' : 'inactive'}`}>
                  {form.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td>{new Date(form.created_at).toLocaleDateString()}</td>
              <td>
                <button onClick={() => { setEditingForm(form); setShowModal(true); }}>Edit</button>
                <button onClick={() => duplicateForm(form)}>Duplicate</button>
                <button onClick={() => toggleActive(form.id, form.is_active)}>
                  {form.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button onClick={() => deleteForm(form.id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && <FormBuilderModal form={editingForm} onClose={() => { setShowModal(false); loadForms(); }} />}
    </div>
  );
}

function FormBuilderModal({ form, onClose }) {
  const [title, setTitle] = useState(form?.title || '');
  const [description, setDescription] = useState(form?.description || '');
  const [sections, setSections] = useState(form?.fields?.sections || [{ title: 'Section 1', fields: [] }]);

  const addSection = () => setSections([...sections, { title: `Section ${sections.length + 1}`, fields: [] }]);

  const updateSection = (idx, key, value) => {
    const updated = [...sections];
    updated[idx][key] = value;
    setSections(updated);
  };

  const removeSection = (idx) => setSections(sections.filter((_, i) => i !== idx));

  const addField = (sectionIdx) => {
    const updated = [...sections];
    updated[sectionIdx].fields.push({ label: '', type: 'TEXT', required: false });
    setSections(updated);
  };

  const updateField = (sectionIdx, fieldIdx, key, value) => {
    const updated = [...sections];
    updated[sectionIdx].fields[fieldIdx][key] = value;
    setSections(updated);
  };

  const removeField = (sectionIdx, fieldIdx) => {
    const updated = [...sections];
    updated[sectionIdx].fields.splice(fieldIdx, 1);
    setSections(updated);
  };

  const save = async () => {
    if (!title) {
      alert('Please enter a form title');
      return;
    }
    
    const data = { title, description, fields: { sections } };
    console.log('Saving form:', data);
    
    try {
      let result;
      if (form) {
        result = await supabase.from('evaluation_forms').update(data).eq('id', form.id).select();
      } else {
        result = await supabase.from('evaluation_forms').insert([data]).select();
      }
      
      console.log('Save result:', result);
      
      if (result.error) {
        console.error('Save error:', result.error);
        alert('Error saving form: ' + result.error.message);
        return;
      }
      
      alert('Form saved successfully!');
      onClose();
    } catch (err) {
      console.error('Exception:', err);
      alert('Error: ' + err.message);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <h2>{form ? 'Edit' : 'Create'} Evaluation Form</h2>
        
        <input placeholder="Form Title" value={title} onChange={e => setTitle(e.target.value)} />
        <textarea placeholder="Description" value={description} onChange={e => setDescription(e.target.value)} />

        {sections.map((section, sIdx) => (
          <div key={sIdx} className="section-editor">
            <div className="section-header">
              <input placeholder="Section Title" value={section.title} onChange={e => updateSection(sIdx, 'title', e.target.value)} />
              <button onClick={() => removeSection(sIdx)}>Remove Section</button>
            </div>
            <textarea 
              placeholder="Section Description (optional)" 
              value={section.description || ''} 
              onChange={e => updateSection(sIdx, 'description', e.target.value)}
              style={{width: '100%', marginBottom: '10px', minHeight: '60px'}}
            />
            <input 
              placeholder="Scale Legend (e.g., 'Performance Rating') - shown if section has scale questions" 
              value={section.scaleLegend || ''} 
              onChange={e => updateSection(sIdx, 'scaleLegend', e.target.value)}
              style={{width: '100%', marginBottom: '10px'}}
            />

            {/* Scale Questions Table */}
            {section.fields.some(f => f.type === 'SCALE') && (
              <div style={{marginBottom: '20px', padding: '10px', background: '#f0f8ff', borderRadius: '5px'}}>
                <h4 style={{marginBottom: '10px'}}>Scale Questions (1-5)</h4>
                {section.fields.map((field, fIdx) => {
                  if (field.type !== 'SCALE') return null;
                  return (
                    <div key={fIdx} style={{display: 'flex', gap: '10px', marginBottom: '5px', alignItems: 'center'}}>
                      <input 
                        placeholder="Question" 
                        value={field.label} 
                        onChange={e => updateField(sIdx, fIdx, 'label', e.target.value)}
                        style={{flex: 1}}
                      />
                      <label style={{display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap'}}>
                        <input 
                          type="checkbox" 
                          checked={field.required} 
                          onChange={e => updateField(sIdx, fIdx, 'required', e.target.checked)} 
                        />
                        Required
                      </label>
                      <button onClick={() => removeField(sIdx, fIdx)} style={{padding: '5px 10px'}}>✕</button>
                    </div>
                  );
                })}
                <button onClick={() => {
                  const updated = [...sections];
                  updated[sIdx].fields.push({ label: '', type: 'SCALE', required: false });
                  setSections(updated);
                }} style={{marginTop: '10px'}}>+ Add Scale Question</button>
              </div>
            )}

            {/* Other Question Types */}
            {section.fields.map((field, fIdx) => {
              if (field.type === 'SCALE') return null;
              return (
              <div key={fIdx} className="field-editor">
                <input placeholder="Question" value={field.label} onChange={e => updateField(sIdx, fIdx, 'label', e.target.value)} />
                <select value={field.type} onChange={e => updateField(sIdx, fIdx, 'type', e.target.value)}>
                  <option value="TEXT">Text</option>
                  <option value="PARAGRAPH_TEXT">Paragraph</option>
                  <option value="MULTIPLE_CHOICE">Single Choice (Radio)</option>
                  <option value="CHECKBOX">Multiple Choice (Checkbox)</option>
                  <option value="SCALE">Scale (1-5)</option>
                  <option value="DATE">Date</option>
                </select>
                {(field.type === 'MULTIPLE_CHOICE' || field.type === 'CHECKBOX') && (
                  <textarea 
                    placeholder="Options (one per line)" 
                    value={field.options?.join('\n') || ''} 
                    onChange={e => {
                      const lines = e.target.value.split('\n');
                      updateField(sIdx, fIdx, 'options', lines);
                    }}
                    rows="3"
                    style={{width: '100%'}}
                  />
                )}
                <label>
                  <input type="checkbox" checked={field.required} onChange={e => updateField(sIdx, fIdx, 'required', e.target.checked)} />
                  Required
                </label>
                <button onClick={() => removeField(sIdx, fIdx)}>Remove</button>
              </div>
              );
            })}

            <button onClick={() => addField(sIdx)}>Add Other Question Type</button>
          </div>
        ))}

        <button onClick={addSection}>Add Section</button>

        <div className="modal-actions">
          <button onClick={save}>Save</button>
          <button onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function TeachersTab() {
  const [teachers, setTeachers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    loadTeachers();
  }, []);

  const loadTeachers = async () => {
    const { data } = await supabase.from('teachers').select('*').order('name');
    setTeachers(data || []);
  };

  const deleteTeacher = async (id) => {
    if (window.confirm('Delete this teacher?')) {
      await supabase.from('teachers').delete().eq('id', id);
      loadTeachers();
    }
  };

  const toggleActive = async (id, isActive) => {
    await supabase.from('teachers').update({ is_active: !isActive }).eq('id', id);
    loadTeachers();
  };

  return (
    <div>
      <button className="btn-primary" onClick={() => { setEditing(null); setShowModal(true); }}>
        Add Teacher
      </button>

      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Department</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {teachers.map(teacher => (
            <tr key={teacher.id}>
              <td>{teacher.name}</td>
              <td>{teacher.department}</td>
              <td>
                <span className={`status ${teacher.is_active ? 'active' : 'inactive'}`}>
                  {teacher.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td>
                <button onClick={() => { setEditing(teacher); setShowModal(true); }}>Edit</button>
                <button onClick={() => toggleActive(teacher.id, teacher.is_active)}>
                  {teacher.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button onClick={() => deleteTeacher(teacher.id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && <TeacherModal teacher={editing} onClose={() => { setShowModal(false); loadTeachers(); }} />}
    </div>
  );
}

function TeacherModal({ teacher, onClose }) {
  const [name, setName] = useState(teacher?.name || '');
  const [department, setDepartment] = useState(teacher?.department || '');

  const save = async () => {
    const data = { name, department };
    if (teacher) {
      await supabase.from('teachers').update(data).eq('id', teacher.id);
    } else {
      await supabase.from('teachers').insert(data);
    }
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h2>{teacher ? 'Edit' : 'Add'} Teacher</h2>
        <input placeholder="Name" value={name} onChange={e => setName(e.target.value)} required />
        <select value={department} onChange={e => setDepartment(e.target.value)} required>
          <option value="">Select Department</option>
          <option value="BED">BED - Basic Education</option>
          <option value="JHS">JHS - Junior Highschool</option>
          <option value="SHS">SHS - Senior Highschool</option>
          <option value="HED">HED - Higher Education</option>
        </select>
        <div className="modal-actions">
          <button onClick={save}>Save</button>
          <button onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function SubjectsTab() {
  const [subjects, setSubjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    loadSubjects();
  }, []);

  const loadSubjects = async () => {
    const { data } = await supabase.from('subjects').select('*').order('code');
    setSubjects(data || []);
  };

  const deleteSubject = async (id) => {
    if (window.confirm('Delete this subject?')) {
      await supabase.from('subjects').delete().eq('id', id);
      loadSubjects();
    }
  };

  return (
    <div>
      <button className="btn-primary" onClick={() => { setEditing(null); setShowModal(true); }}>
        Add Subject
      </button>

      <table>
        <thead>
          <tr>
            <th>Code</th>
            <th>Name</th>
            <th>Department</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {subjects.map(subject => (
            <tr key={subject.id}>
              <td>{subject.code}</td>
              <td>{subject.name}</td>
              <td>{subject.department}</td>
              <td>{subject.description}</td>
              <td>
                <button onClick={() => { setEditing(subject); setShowModal(true); }}>Edit</button>
                <button onClick={() => deleteSubject(subject.id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && <SubjectModal subject={editing} onClose={() => { setShowModal(false); loadSubjects(); }} />}
    </div>
  );
}

function SubjectModal({ subject, onClose }) {
  const [code, setCode] = useState(subject?.code || '');
  const [name, setName] = useState(subject?.name || '');
  const [department, setDepartment] = useState(subject?.department || '');
  const [description, setDescription] = useState(subject?.description || '');

  const save = async () => {
    const data = { code, name, department, description };
    if (subject) {
      await supabase.from('subjects').update(data).eq('id', subject.id);
    } else {
      await supabase.from('subjects').insert(data);
    }
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h2>{subject ? 'Edit' : 'Add'} Subject</h2>
        <input placeholder="Subject Code" value={code} onChange={e => setCode(e.target.value)} required />
        <input placeholder="Subject Name" value={name} onChange={e => setName(e.target.value)} required />
        <select value={department} onChange={e => setDepartment(e.target.value)} required>
          <option value="">Select Department</option>
          <option value="BED">BED - Basic Education</option>
          <option value="JHS">JHS - Junior Highschool</option>
          <option value="SHS">SHS - Senior Highschool</option>
          <option value="HED">HED - Higher Education</option>
        </select>
        <textarea placeholder="Description" value={description} onChange={e => setDescription(e.target.value)} />
        <div className="modal-actions">
          <button onClick={save}>Save</button>
          <button onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function StudentAccountsTab() {
  const [students, setStudents] = useState([]);
  const [filter, setFilter] = useState('pending');

  useEffect(() => {
    loadStudents();
  }, [filter]);

  const loadStudents = async () => {
    let query = supabase.from('student_accounts').select('*').order('created_at', { ascending: false });
    if (filter !== 'all') query = query.eq('status', filter);
    const { data } = await query;
    setStudents(data || []);
  };

  const updateStatus = async (id, status) => {
    await supabase.from('student_accounts').update({ status, approved_at: new Date().toISOString() }).eq('id', id);
    loadStudents();
  };

  const deleteStudent = async (id) => {
    if (window.confirm('Delete this student account? This action cannot be undone.')) {
      const { error } = await supabase.from('student_accounts').delete().eq('id', id);
      if (error) {
        toast.error('Failed to delete: ' + error.message);
      } else {
        toast.success('Student account deleted');
        loadStudents();
      }
    }
  };

  return (
    <div>
      <div className="filters">
        <button className={filter === 'pending' ? 'active' : ''} onClick={() => setFilter('pending')}>Pending</button>
        <button className={filter === 'approved' ? 'active' : ''} onClick={() => setFilter('approved')}>Approved</button>
        <button className={filter === 'rejected' ? 'active' : ''} onClick={() => setFilter('rejected')}>Rejected</button>
        <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>All</button>
      </div>

      <table>
        <thead>
          <tr>
            <th>Student ID</th>
            <th>Name</th>
            <th>Email</th>
            <th>Campus</th>
            <th>Department</th>
            <th>Level</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {students.map(student => (
            <tr key={student.id}>
              <td>{student.student_id}</td>
              <td>{student.name}</td>
              <td>{student.email}</td>
              <td>{student.campus}</td>
              <td>{student.department}</td>
              <td>{student.level}</td>
              <td><span className={`status ${student.status}`}>{student.status}</span></td>
              <td>{new Date(student.created_at).toLocaleDateString()}</td>
              <td>
                {student.status === 'pending' && (
                  <>
                    <button onClick={() => updateStatus(student.id, 'approved')}>Approve</button>
                    <button onClick={() => updateStatus(student.id, 'rejected')}>Reject</button>
                  </>
                )}
                {student.status === 'approved' && (
                  <button onClick={() => deleteStudent(student.id)} style={{background: '#dc2626'}}>Delete</button>
                )}
                {student.status === 'rejected' && (
                  <button onClick={() => deleteStudent(student.id)} style={{background: '#dc2626'}}>Delete</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AssignmentsTab({ initialView = 'teacher-subject', initialStatusFilter = '' }) {
  const [view, setView] = useState(initialView);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [forms, setForms] = useState([]);
  const [defaultForm, setDefaultForm] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [filters, setFilters] = useState({ teacherId: '', subjectId: '', campus: '', department: '', level: '', status: initialStatusFilter });
  const [evaluations, setEvaluations] = useState([]);

  useEffect(() => {
    setView(initialView);
    setFilters(prev => ({ ...prev, status: initialStatusFilter }));
  }, [initialView, initialStatusFilter]);

  useEffect(() => {
    setFilters({ teacherId: '', subjectId: '', campus: '', department: '', level: '', status: '' });
    loadData();
  }, [view]);

  useEffect(() => {
    applyFilters();
  }, [assignments, filters]);

  const applyFilters = () => {
    let filtered = [...assignments];
    
    if (view === 'teacher-subject') {
      if (filters.teacherId) filtered = filtered.filter(a => a.teacher_id === filters.teacherId);
      if (filters.subjectId) filtered = filtered.filter(a => a.subject_id === filters.subjectId);
      if (filters.campus) filtered = filtered.filter(a => a.campus === filters.campus);
      if (filters.department) filtered = filtered.filter(a => a.department === filters.department);
      if (filters.level) filtered = filtered.filter(a => a.level === filters.level);
    } else {
      if (filters.teacherId) filtered = filtered.filter(a => a.teacher_id === filters.teacherId);
      if (filters.subjectId) filtered = filtered.filter(a => a.subject_id === filters.subjectId);
      
      // Status filter for student-subject view
      if (filters.status) {
        const assignmentIds = filtered.map(a => `${a.student_id}_${a.teacher_id}_${a.subject_id}_${a.academic_year}_${a.semester}`);
        const evaluatedIds = evaluations.map(e => `${e.student_id}_${e.teacher_id}_${e.subject_id}_${e.academic_year}_${e.semester}`);
        
        if (filters.status === 'completed') {
          filtered = filtered.filter((a, idx) => evaluatedIds.includes(assignmentIds[idx]));
        } else if (filters.status === 'pending') {
          filtered = filtered.filter((a, idx) => !evaluatedIds.includes(assignmentIds[idx]));
        }
      }
    }
    
    console.log('Applying filters:', filters);
    console.log('Total assignments:', assignments.length);
    console.log('Filtered assignments:', filtered.length);
    
    setFilteredAssignments(filtered);
  };

  const getLevelOptions = () => {
    switch(filters.department) {
      case 'BED': return ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6'];
      case 'JHS': return ['Grade 7', 'Grade 8', 'Grade 9', 'Grade 10'];
      case 'SHS': return ['Grade 11', 'Grade 12'];
      case 'HED': return ['College'];
      default: return [];
    }
  };

  const loadData = async () => {
    const [t, s, st, f] = await Promise.all([
      supabase.from('teachers').select('*').eq('is_active', true),
      supabase.from('subjects').select('*'),
      supabase.from('student_accounts').select('*').eq('status', 'approved'),
      supabase.from('evaluation_forms').select('*').order('created_at', { ascending: false })
    ]);
    setTeachers(t.data || []);
    setSubjects(s.data || []);
    setStudents(st.data || []);
    setForms(f.data || []);
    
    const activeForm = (f.data || []).find(form => form.is_active);
    setDefaultForm(activeForm);

    if (view === 'teacher-subject') {
      const { data } = await supabase.from('teacher_subjects').select('*, teachers(name), subjects(code, name), evaluation_forms(title)').order('created_at', { ascending: false });
      setAssignments(data || []);
    } else {
      const { data } = await supabase.from('student_subjects').select('*, student_accounts(student_id, name), subjects(code, name), teachers(name)').order('created_at', { ascending: false });
      setAssignments(data || []);
      
      // Load evaluations for status checking
      const evals = await fetchAllRows(() => supabase.from('teacher_evaluations').select('student_id, teacher_id, subject_id, academic_year, semester'));
      setEvaluations(evals);
    }
  };

  const deleteAssignment = async (id) => {
    const table = view === 'teacher-subject' ? 'teacher_subjects' : 'student_subjects';
    await supabase.from(table).delete().eq('id', id);
    loadData();
  };

  return (
    <div>
      <div className="filters">
        <button className={view === 'teacher-subject' ? 'active' : ''} onClick={() => setView('teacher-subject')}>
          Teacher-Subject
        </button>
        <button className={view === 'student-subject' ? 'active' : ''} onClick={() => setView('student-subject')}>
          Student-Subject
        </button>
      </div>

      {/* Filters */}
      <div className="filters" style={{marginTop: '10px', marginBottom: '20px'}}>
        <select value={filters.teacherId} onChange={e => setFilters({...filters, teacherId: e.target.value})}>
          <option value="">All Teachers</option>
          {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={filters.subjectId} onChange={e => setFilters({...filters, subjectId: e.target.value})}>
          <option value="">All Subjects</option>
          {subjects.map(s => <option key={s.id} value={s.id}>{s.code} - {s.name}</option>)}
        </select>
        {view === 'teacher-subject' && (
          <>
            <select value={filters.campus} onChange={e => setFilters({...filters, campus: e.target.value})}>
              <option value="">All Campus</option>
              <option value="Gubat">Gubat</option>
              <option value="Main">Main</option>
            </select>
            <select value={filters.department} onChange={e => setFilters({...filters, department: e.target.value, level: ''})}>
              <option value="">All Departments</option>
              <option value="BED">BED</option>
              <option value="JHS">JHS</option>
              <option value="SHS">SHS</option>
              <option value="HED">HED</option>
            </select>
            {filters.department && (
              <select value={filters.level} onChange={e => setFilters({...filters, level: e.target.value})}>
                <option value="">All Levels</option>
                {getLevelOptions().map(lvl => <option key={lvl} value={lvl}>{lvl}</option>)}
              </select>
            )}
          </>
        )}
        {view === 'student-subject' && (
          <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})}>
            <option value="">All Status</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
          </select>
        )}
        <button onClick={() => setFilters({ teacherId: '', subjectId: '', campus: '', department: '', level: '', status: '' })}>Clear Filters</button>
      </div>

      <button className="btn-primary" onClick={() => { setEditingAssignment(null); setShowModal(true); }}>
        Add Assignment
      </button>

      <table>
        <thead>
          <tr>
            {view === 'teacher-subject' ? (
              <>
                <th>Teacher Name</th>
                <th>Subject</th>
                <th>Campus</th>
                <th>Department</th>
                <th>Level</th>
                <th>Form</th>
              </>
            ) : (
              <>
                <th>Student</th>
                <th>Subject</th>
                <th>Teacher Name</th>
                <th>Status</th>
              </>
            )}
            <th>Academic Year</th>
            <th>Semester</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredAssignments.map(a => {
            const assignmentKey = `${a.student_id}_${a.teacher_id}_${a.subject_id}_${a.academic_year}_${a.semester}`;
            const isEvaluated = evaluations.some(e => 
              `${e.student_id}_${e.teacher_id}_${e.subject_id}_${e.academic_year}_${e.semester}` === assignmentKey
            );
            
            return (
            <tr key={a.id}>
              {view === 'teacher-subject' ? (
                <>
                  <td>{a.teachers?.name}</td>
                  <td>{a.subjects?.code} - {a.subjects?.name}</td>
                  <td>{a.campus}</td>
                  <td>{a.department}</td>
                  <td>{a.level}</td>
                  <td>{a.evaluation_forms?.title || (defaultForm?.title + ' (default)') || 'No active form'}</td>
                </>
              ) : (
                <>
                  <td>{a.student_accounts?.student_id} - {a.student_accounts?.name}</td>
                  <td>{a.subjects?.code} - {a.subjects?.name}</td>
                  <td>{a.teachers?.name}</td>
                  <td>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: isEvaluated ? '#d1fae5' : '#fee2e2',
                      color: isEvaluated ? '#065f46' : '#991b1b'
                    }}>
                      {isEvaluated ? '✓ Completed' : '⏳ Pending'}
                    </span>
                  </td>
                </>
              )}
              <td>{a.academic_year}</td>
              <td>{a.semester}</td>
              <td>
                <button onClick={() => { setEditingAssignment(a); setShowModal(true); }}>Edit</button>
                <button onClick={() => deleteAssignment(a.id)}>Delete</button>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>

      {showModal && (
        <AssignmentModal 
          view={view} 
          teachers={teachers} 
          subjects={subjects} 
          students={students}
          forms={forms}
          assignment={editingAssignment}
          onClose={() => { setShowModal(false); setEditingAssignment(null); loadData(); }} 
        />
      )}
    </div>
  );
}

function AssignmentModal({ view, teachers, subjects, students, forms, assignment, onClose }) {
  const [campus, setCampus] = useState(assignment?.campus || '');
  const [department, setDepartment] = useState(assignment?.department || '');
  const [level, setLevel] = useState(assignment?.level || '');
  const [teacherId, setTeacherId] = useState(assignment?.teacher_id || '');
  const [subjectId, setSubjectId] = useState(assignment?.subject_id || '');
  const [studentId, setStudentId] = useState(assignment?.student_id || '');
  const [formId, setFormId] = useState(assignment?.form_id || (forms.length > 0 ? forms[0].id : ''));
  const [academicYear, setAcademicYear] = useState(assignment?.academic_year || '2024-2025');
  const [semester, setSemester] = useState(assignment?.semester || '');

  useEffect(() => {
    if (!assignment && department) {
      // Set default semester based on department
      if (department === 'BED' || department === 'JHS') {
        setSemester('1st Quarter');
      } else {
        setSemester('1st Semester');
      }
    }
  }, [department, assignment]);

  const getSemesterOptions = () => {
    if (department === 'BED' || department === 'JHS') {
      return ['1st Quarter', '2nd Quarter', '3rd Quarter', '4th Quarter', 'Summer'];
    }
    return ['1st Semester', '2nd Semester', 'Summer'];
  };

  const getLevelOptions = () => {
    switch(department) {
      case 'BED': return ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6'];
      case 'JHS': return ['Grade 7', 'Grade 8', 'Grade 9', 'Grade 10'];
      case 'SHS': return ['Grade 11', 'Grade 12'];
      case 'HED': return ['College'];
      default: return [];
    }
  };

  // Filter teachers by department
  const filteredTeachers = department ? teachers.filter(t => t.department === department) : [];
  
  // Filter subjects by department only
  const filteredSubjects = department ? subjects.filter(s => s.department === department) : [];

  const save = async (e) => {
    e.preventDefault();
    if (view === 'teacher-subject') {
      const data = { 
        teacher_id: teacherId, 
        subject_id: subjectId, 
        form_id: formId,
        campus,
        department,
        level,
        academic_year: academicYear, 
        semester 
      };
      if (assignment) {
        await supabase.from('teacher_subjects').update(data).eq('id', assignment.id);
      } else {
        await supabase.from('teacher_subjects').insert(data);
      }
    } else {
      const data = { 
        student_id: studentId, 
        subject_id: subjectId, 
        teacher_id: teacherId, 
        academic_year: academicYear, 
        semester 
      };
      if (assignment) {
        await supabase.from('student_subjects').update(data).eq('id', assignment.id);
      } else {
        await supabase.from('student_subjects').insert(data);
      }
    }
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h2>{assignment ? 'Edit' : 'Add'} {view === 'teacher-subject' ? 'Teacher-Subject' : 'Student-Subject'} Assignment</h2>
        
        <form onSubmit={save}>
          {view === 'teacher-subject' && (
            <>
              <select value={campus} onChange={e => setCampus(e.target.value)} required>
                <option value="">Select Campus</option>
                <option value="Gubat">Gubat</option>
                <option value="Main">Main</option>
              </select>
              <select value={department} onChange={e => { setDepartment(e.target.value); setLevel(''); setTeacherId(''); setSubjectId(''); }} required>
                <option value="">Select Department</option>
                <option value="BED">BED - Basic Education</option>
                <option value="JHS">JHS - Junior Highschool</option>
                <option value="SHS">SHS - Senior Highschool</option>
                <option value="HED">HED - Higher Education</option>
              </select>
              <select value={level} onChange={e => { setLevel(e.target.value); }} required disabled={!department}>
                <option value="">Select Level</option>
                {getLevelOptions().map(lvl => <option key={lvl} value={lvl}>{lvl}</option>)}
              </select>
              <select value={teacherId} onChange={e => setTeacherId(e.target.value)} disabled={!department || !!assignment} required>
                <option value="">Select Teacher</option>
                {filteredTeachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <select value={subjectId} onChange={e => setSubjectId(e.target.value)} disabled={!department || !!assignment} required>
                <option value="">Select Subject</option>
                {filteredSubjects.map(s => <option key={s.id} value={s.id}>{s.code} - {s.name}</option>)}
              </select>
              <label style={{fontSize: '12px', color: '#666', marginTop: '10px'}}>Evaluation Form</label>
              <select value={formId} onChange={e => setFormId(e.target.value)} required>
                <option value="">Select Form</option>
                {forms.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.title} {f.is_active ? '★' : ''}
                  </option>
                ))}
              </select>
            </>
          )}

          {view === 'student-subject' && (
            <>
              <select value={studentId} onChange={e => setStudentId(e.target.value)} disabled={!!assignment}>
                <option value="">Select Student</option>
                {students.map(s => <option key={s.id} value={s.id}>{s.student_id} - {s.name}</option>)}
              </select>
              <select value={subjectId} onChange={e => setSubjectId(e.target.value)} disabled={!!assignment} required>
                <option value="">Select Subject</option>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.code} - {s.name}</option>)}
              </select>
              <select value={teacherId} onChange={e => setTeacherId(e.target.value)} disabled={!!assignment} required>
                <option value="">Select Teacher</option>
                {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </>
          )}

          <select value={academicYear} onChange={e => setAcademicYear(e.target.value)}>
            <option>2024-2025</option>
            <option>2025-2026</option>
            <option>2026-2027</option>
            <option>2027-2028</option>
            <option>2023-2024</option>
            <option>2022-2023</option>
          </select>
          
          <select value={semester} onChange={e => setSemester(e.target.value)}>
            {getSemesterOptions().map(opt => (
              <option key={opt}>{opt}</option>
            ))}
          </select>

          <div className="modal-actions">
            <button type="submit">Save</button>
            <button type="button" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const SUBS_PAGE_SIZE = 50;

function SubmissionsTab() {
  const [submissions, setSubmissions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [summary, setSummary] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({ teacherId: '', subjectId: '', campus: '', academicYear: '', semester: '' });

  useEffect(() => {
    loadFilters();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [filters, page]); // eslint-disable-line

  const loadFilters = async () => {
    const [t, s] = await Promise.all([
      supabase.from('teachers').select('*').order('name'),
      supabase.from('subjects').select('*').order('code')
    ]);
    setTeachers(t.data || []);
    setSubjects(s.data || []);
  };

  const applyFilters = (q, campusStudentIds = null) => {
    if (filters.teacherId) q = q.eq('teacher_id', filters.teacherId);
    if (filters.subjectId) q = q.eq('subject_id', filters.subjectId);
    if (filters.academicYear) q = q.eq('academic_year', filters.academicYear);
    if (filters.semester) q = q.eq('semester', filters.semester);
    if (campusStudentIds) q = q.in('student_id', campusStudentIds);
    return q;
  };

  const loadData = async () => {
    // Resolve campus filter to student IDs
    let campusStudentIds = null;
    if (filters.campus) {
      const { data: campusStudents } = await supabase
        .from('student_accounts').select('id').eq('campus', filters.campus);
      campusStudentIds = (campusStudents || []).map(s => s.id);
      if (campusStudentIds.length === 0) {
        setTotalCount(0); setSubmissions([]); return;
      }
    }

    // Count total (for pagination display)
    let countQ = supabase.from('teacher_evaluations').select('*', { count: 'exact', head: true });
    countQ = applyFilters(countQ, campusStudentIds);
    const { count } = await countQ;
    setTotalCount(count || 0);

    // Fetch current page
    const from = page * SUBS_PAGE_SIZE;
    const to = from + SUBS_PAGE_SIZE - 1;
    let subsQ = supabase
      .from('teacher_evaluations')
      .select('id, submitted_at, academic_year, semester, responses, teachers(name), subjects(code, name), student_accounts(student_id, name), evaluation_forms(title, fields)')
      .order('submitted_at', { ascending: false })
      .range(from, to);
    subsQ = applyFilters(subsQ, campusStudentIds);
    const { data: subs, error } = await subsQ;
    if (error) console.error('Submissions query error:', error);
    setSubmissions(subs || []);

    // Load summary from materialized view (fast — pre-computed, no full table scan)
    let summaryQ = supabase.from('mv_evaluation_summary').select('*');
    if (filters.teacherId)    summaryQ = summaryQ.eq('teacher_id',    filters.teacherId);
    if (filters.subjectId)    summaryQ = summaryQ.eq('subject_id',    filters.subjectId);
    if (filters.academicYear) summaryQ = summaryQ.eq('academic_year', filters.academicYear);
    if (filters.semester)     summaryQ = summaryQ.eq('semester',      filters.semester);
    const { data: summaryData } = await summaryQ;

    setSummary((summaryData || []).map(s => ({
      teacher:      s.teacher_name,
      subject:      `${s.subject_code} - ${s.subject_name}`,
      academicYear: s.academic_year,
      semester:     s.semester,
      enrolled:     s.enrolled_count,
      submitted:    s.submitted_count,
    })));
  };

  const deleteSubmission = async (id) => {
    if (window.confirm('Are you sure you want to delete this evaluation submission?')) {
      const { error } = await supabase.from('teacher_evaluations').delete().eq('id', id);
      if (error) {
        toast.error('Failed to delete submission: ' + error.message);
        console.error('Delete error:', error);
      } else {
        toast.success('Submission deleted successfully');
        await loadData();
      }
    }
  };

  return (
    <div>
      <div className="filters" style={{marginBottom: '20px'}}>
        <select value={filters.campus} onChange={e => setFilters({...filters, campus: e.target.value})}>
          <option value="">All Campus</option>
          <option value="Gubat">Gubat</option>
          <option value="Main">Main</option>
        </select>
        <select value={filters.teacherId} onChange={e => setFilters({...filters, teacherId: e.target.value})}>
          <option value="">All Teachers</option>
          {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={filters.subjectId} onChange={e => setFilters({...filters, subjectId: e.target.value})}>
          <option value="">All Subjects</option>
          {subjects.map(s => <option key={s.id} value={s.id}>{s.code} - {s.name}</option>)}
        </select>
        <select value={filters.academicYear} onChange={e => setFilters({...filters, academicYear: e.target.value})}>
          <option value="">All Academic Years</option>
          <option>2024-2025</option>
          <option>2025-2026</option>
          <option>2023-2024</option>
          <option>2022-2023</option>
        </select>
        <select value={filters.semester} onChange={e => setFilters({...filters, semester: e.target.value})}>
          <option value="">All Semesters/Quarters</option>
          <option>1st Semester</option>
          <option>2nd Semester</option>
          <option>1st Quarter</option>
          <option>2nd Quarter</option>
          <option>3rd Quarter</option>
          <option>4th Quarter</option>
          <option>Summer</option>
        </select>
        <button onClick={() => setFilters({ teacherId: '', subjectId: '', campus: '', academicYear: '', semester: '' })}>Clear Filters</button>
      </div>

      <h2>Evaluation Summary</h2>
      <table style={{marginBottom: '40px'}}>
        <thead>
          <tr>
            <th>Teacher</th>
            <th>Subject</th>
            <th>Academic Year</th>
            <th>Semester</th>
            <th>Enrolled Students</th>
            <th>Submitted</th>
            <th>Pending</th>
            <th>Progress</th>
          </tr>
        </thead>
        <tbody>
          {summary.map((s, idx) => (
            <tr key={idx}>
              <td>{s.teacher}</td>
              <td>{s.subject}</td>
              <td>{s.academicYear}</td>
              <td>{s.semester}</td>
              <td>{s.enrolled}</td>
              <td>{s.submitted}</td>
              <td>{s.enrolled - s.submitted}</td>
              <td>
                <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <div style={{flex: 1, height: '20px', background: '#e0e0e0', borderRadius: '10px', overflow: 'hidden'}}>
                    <div style={{width: `${s.enrolled > 0 ? (s.submitted / s.enrolled * 100) : 0}%`, height: '100%', background: '#4caf50'}}></div>
                  </div>
                  <span>{s.enrolled > 0 ? Math.round(s.submitted / s.enrolled * 100) : 0}%</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>All Submissions</h2>
      <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px'}}>
        <span style={{fontSize: '14px', color: '#555'}}>
          {totalCount} total — showing {page * SUBS_PAGE_SIZE + 1}–{Math.min((page + 1) * SUBS_PAGE_SIZE, totalCount)}
        </span>
        <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
        <span style={{fontSize: '14px'}}>Page {page + 1} of {Math.max(1, Math.ceil(totalCount / SUBS_PAGE_SIZE))}</span>
        <button disabled={(page + 1) * SUBS_PAGE_SIZE >= totalCount} onClick={() => setPage(p => p + 1)}>Next →</button>
      </div>
      <table>
        <thead>
          <tr>
            <th>Submitted</th>
            <th>Student</th>
            <th>Teacher</th>
            <th>Subject</th>
            <th>Form</th>
            <th>Academic Year</th>
            <th>Semester</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {submissions.map(sub => (
            <tr key={sub.id}>
              <td>{new Date(sub.submitted_at).toLocaleString()}</td>
              <td>{sub.student_accounts?.student_id} - {sub.student_accounts?.name}</td>
              <td>{sub.teachers?.name}</td>
              <td>{sub.subjects?.code} - {sub.subjects?.name}</td>
              <td>{sub.evaluation_forms?.title}</td>
              <td>{sub.academic_year}</td>
              <td>{sub.semester}</td>
              <td>
                <button onClick={() => { setSelectedSubmission(sub); setShowModal(true); }}>View Responses</button>
                <button onClick={() => deleteSubmission(sub.id)} style={{marginLeft: '5px', background: '#dc2626'}}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && selectedSubmission && (
        <ResponseModal submission={selectedSubmission} onClose={() => { setShowModal(false); setSelectedSubmission(null); }} />
      )}
    </div>
  );
}

function ResponseModal({ submission, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <h2>Evaluation Responses</h2>
        <div style={{marginBottom: '20px'}}>
          <p><strong>Student:</strong> {submission.student_accounts?.student_id} - {submission.student_accounts?.name}</p>
          <p><strong>Teacher:</strong> {submission.teachers?.name}</p>
          <p><strong>Subject:</strong> {submission.subjects?.code} - {submission.subjects?.name}</p>
          <p><strong>Form:</strong> {submission.evaluation_forms?.title}</p>
          <p><strong>Submitted:</strong> {new Date(submission.submitted_at).toLocaleString()}</p>
        </div>

        <div style={{maxHeight: '60vh', overflowY: 'auto'}}>
          {submission.evaluation_forms?.fields?.sections?.map((section, sIdx) => (
            <div key={sIdx} style={{marginBottom: '30px', padding: '15px', background: '#f9f9f9', borderRadius: '8px'}}>
              <h3 style={{marginBottom: '15px', color: '#333'}}>{section.title}</h3>
              {section.fields?.map((field, fIdx) => {
                const fieldKey = `section_${sIdx}_field_${fIdx}`;
                const response = submission.responses[fieldKey];
                return (
                  <div key={fIdx} style={{marginBottom: '15px', padding: '10px', background: 'white', borderRadius: '5px'}}>
                    <p style={{fontWeight: 'bold', marginBottom: '5px'}}>{field.label}</p>
                    <p style={{color: '#555'}}>
                      {Array.isArray(response) ? response.join(', ') : (response || 'No response')}
                    </p>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="modal-actions">
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function AnalyticsTab() {
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedCampus, setSelectedCampus] = useState('');
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportingEvals, setExportingEvals] = useState(false);

  useEffect(() => {
    loadFilters();
  }, []);

  useEffect(() => {
    loadResults();
  }, [selectedTeacher, selectedSubject, selectedDepartment, selectedCampus, selectedAcademicYear, selectedSemester]); // eslint-disable-line
  const loadFilters = async () => {
    const [t, s] = await Promise.all([
      supabase.from('teachers').select('*').order('name'),
      supabase.from('subjects').select('*').order('code')
    ]);
    setTeachers(t.data || []);
    setSubjects(s.data || []);
    loadResults();
  };

  const analyzeSentiment = (text) => {
    if (!text) return 'neutral';
    const lowerText = text.toLowerCase();
    
    const positiveWords = [
      // English positive words
      'excellent', 'great', 'good', 'amazing', 'wonderful', 'best', 'helpful', 'clear', 'effective', 'engaging', 'knowledgeable', 'patient', 'understanding', 'inspiring', 'dedicated', 'professional', 'organized', 'fair', 'respectful', 'supportive',
      // Filipino/Tagalog positive words
      'magaling', 'mabuti', 'mahusay', 'napakagaling', 'maganda', 'astig', 'galing', 'husay', 'maayos', 'marunong', 'matalino', 'masipag', 'matulungin', 'mabait', 'magturo', 'nakakatuwa', 'inspiring', 'napakahusay', 'perpekto', 'swabe'
    ];
    const negativeWords = [
      // English negative words
      'poor', 'bad', 'terrible', 'worst', 'boring', 'confusing', 'unclear', 'ineffective', 'unprofessional', 'disorganized', 'unfair', 'rude', 'difficult', 'harsh', 'strict', 'late', 'absent','ego',
      // Filipino/Tagalog negative words
      'pangit', 'masama', 'hindi maganda', 'walang kwenta', 'boring', 'nakakainis', 'nakakaboring', 'mahirap', 'masakit', 'bastos', 'suplado', 'suplada', 'tamad', 'late', 'absent', 'ego', 'mayabang', 'hambog', 'walang galang', 'terror'
    ];
    
    let positiveCount = 0;
    let negativeCount = 0;
    
    positiveWords.forEach(word => {
      if (lowerText.includes(word)) positiveCount++;
    });
    
    negativeWords.forEach(word => {
      if (lowerText.includes(word)) negativeCount++;
    });
    
    if (positiveCount > negativeCount) return 'positive';
    if (negativeCount > positiveCount) return 'negative';
    return 'neutral';
  };

  const extractKeywords = (texts) => {
    const stopWords = ['the', 'is', 'at', 'which', 'on', 'a', 'an', 'and', 'or', 'but', 'in', 'with', 'to', 'for', 'of', 'as', 'by', 'that', 'this', 'it', 'from', 'are', 'was', 'were', 'been', 'be', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'can', 'very', 'too', 'also', 'just', 'so', 'than', 'such', 'both', 'through', 'about', 'into', 'during', 'before', 'after', 'above', 'below', 'up', 'down', 'out', 'off', 'over', 'under', 'again', 'further', 'then', 'once', 'here', 'there', 'when', 'where', 'why', 'how', 'all', 'each', 'every', 'some', 'any', 'few', 'more', 'most', 'other', 'another', 'much', 'many', 'own', 'same', 'only', 'his', 'her', 'their', 'our', 'your', 'my', 'me', 'him', 'them', 'us', 'you', 'he', 'she', 'they', 'we', 'i'];
    
    const wordCount = {};
    
    texts.forEach(text => {
      if (!text) return;
      const words = text.toLowerCase().match(/\b[a-z]{3,}\b/g) || [];
      words.forEach(word => {
        if (!stopWords.includes(word)) {
          wordCount[word] = (wordCount[word] || 0) + 1;
        }
      });
    });
    
    return Object.entries(wordCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 30)
      .map(([word, count]) => ({ text: word, value: count }));
  };

  const exportAnalytics = () => {
    if (!results) return;

    const wb = XLSX.utils.book_new();

    // Sheet 1: Summary
    const summaryData = [
      ['Teacher Evaluation Analytics Report'],
      ['Generated:', new Date().toLocaleString()],
      ['Total Evaluations:', results.total],
      [''],
      ['Filters Applied:'],
      ['Campus:', selectedCampus || 'All'],
      ['Department:', selectedDepartment || 'All'],
      ['Teacher:', teachers.find(t => t.id === selectedTeacher)?.name || 'All'],
      ['Subject:', subjects.find(s => s.id === selectedSubject)?.name || 'All'],
      ['Academic Year:', selectedAcademicYear || 'All'],
      ['Semester:', selectedSemester || 'All'],
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

    // Sheet 2: Rating Analytics
    if (results.analytics.length > 0) {
      const analyticsData = [
        ['Question', 'Average Rating', 'Total Responses', '1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars'],
        ...results.analytics.map(item => [
          item.question,
          item.average,
          item.count,
          item.distribution['1'],
          item.distribution['2'],
          item.distribution['3'],
          item.distribution['4'],
          item.distribution['5']
        ])
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(analyticsData);
      XLSX.utils.book_append_sheet(wb, ws2, 'Rating Analytics');
    }

    // Sheet 3: Sentiment Analysis
    const sentimentData = [
      ['Sentiment', 'Count'],
      ['Positive', results.sentimentCounts.positive || 0],
      ['Neutral', results.sentimentCounts.neutral || 0],
      ['Negative', results.sentimentCounts.negative || 0]
    ];
    const ws3 = XLSX.utils.aoa_to_sheet(sentimentData);
    XLSX.utils.book_append_sheet(wb, ws3, 'Sentiment Analysis');

    // Sheet 4: Keywords
    if (results.keywords.length > 0) {
      const keywordsData = [
        ['Keyword', 'Frequency'],
        ...results.keywords.map(k => [k.text, k.value])
      ];
      const ws4 = XLSX.utils.aoa_to_sheet(keywordsData);
      XLSX.utils.book_append_sheet(wb, ws4, 'Keywords');
    }

    // Sheet 5: Department Statistics
    if (results.departmentStats.length > 0) {
      const deptData = [
        ['Department', 'Evaluations', 'Average Rating'],
        ...results.departmentStats.map(d => [d.department, d.count, d.avgRating])
      ];
      const ws5 = XLSX.utils.aoa_to_sheet(deptData);
      XLSX.utils.book_append_sheet(wb, ws5, 'Department Stats');
    }

    // Download
    const fileName = `Analytics_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success('Analytics exported successfully!');
  };

  const exportEvaluations = async () => {
    if (!results) return;
    setExportingEvals(true);
    const toastId = toast.loading('Fetching evaluation data for export…');
    try {
      // Fetch full row data on-demand (only when user explicitly requests export)
      const buildExportQuery = () => {
        let q = supabase.from('teacher_evaluations').select(
          'submitted_at, academic_year, semester, responses, teachers(name), subjects(code, name), student_accounts(student_id, name), evaluation_forms(fields)'
        );
        if (selectedTeacher)      q = q.eq('teacher_id',   selectedTeacher);
        if (selectedSubject)      q = q.eq('subject_id',   selectedSubject);
        if (selectedAcademicYear) q = q.eq('academic_year', selectedAcademicYear);
        if (selectedSemester)     q = q.eq('semester',      selectedSemester);
        return q;
      };
      let rawData = await fetchAllRows(buildExportQuery);
      // Apply campus / department filter client-side (same as Analytics used to do)
      if (selectedCampus)     rawData = rawData.filter(e => e.student_accounts?.campus === selectedCampus);
      if (selectedDepartment) rawData = rawData.filter(e => e.teachers?.department === selectedDepartment);

      if (!rawData || rawData.length === 0) {
        toast.dismiss(toastId);
        toast.error('No evaluation data to export');
        return;
      }

      const wb = XLSX.utils.book_new();
      const evaluationData = [['Student ID', 'Student Name', 'Teacher', 'Subject', 'Academic Year', 'Semester', 'Submitted At']];

      rawData.forEach(evaluation => {
        const row = [
          evaluation.student_accounts?.student_id || 'N/A',
          evaluation.student_accounts?.name || 'N/A',
          evaluation.teachers?.name || 'N/A',
          `${evaluation.subjects?.code} - ${evaluation.subjects?.name}` || 'N/A',
          evaluation.academic_year || 'N/A',
          evaluation.semester || 'N/A',
          new Date(evaluation.submitted_at).toLocaleString()
        ];
        const form = evaluation.evaluation_forms;
        if (form && form.fields && form.fields.sections) {
          form.fields.sections.forEach((section, sIdx) => {
            section.fields.forEach((field, fIdx) => {
              const key = `section_${sIdx}_field_${fIdx}`;
              const value = evaluation.responses[key];
              if (evaluationData.length === 1) evaluationData[0].push(field.label);
              row.push(Array.isArray(value) ? value.join(', ') : (value || ''));
            });
          });
        }
        evaluationData.push(row);
      });

      const ws = XLSX.utils.aoa_to_sheet(evaluationData);
      XLSX.utils.book_append_sheet(wb, ws, 'Evaluations');
      const fileName = `Evaluations_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.dismiss(toastId);
      toast.success('Evaluations exported successfully!');
    } catch (err) {
      toast.dismiss(toastId);
      toast.error('Export failed: ' + err.message);
    } finally {
      setExportingEvals(false);
    }
  };
  const loadResults = async () => {
    setLoading(true);
    try {
      // Helper to apply the active filter set to any view query
      const applyViewFilters = (q) => {
        if (selectedTeacher)      q = q.eq('teacher_id',   selectedTeacher);
        if (selectedSubject)      q = q.eq('subject_id',   selectedSubject);
        if (selectedAcademicYear) q = q.eq('academic_year', selectedAcademicYear);
        if (selectedSemester)     q = q.eq('semester',      selectedSemester);
        if (selectedCampus)       q = q.eq('campus',        selectedCampus);
        if (selectedDepartment)   q = q.eq('teacher_dept',  selectedDepartment);
        return q;
      };

      // 1. Fetch form structure via teacher_evaluations join (avoids RLS block on direct evaluation_forms query)
      const { data: sampleEval } = await supabase
        .from('teacher_evaluations').select('evaluation_forms(fields)').limit(1);
      const form = sampleEval?.[0]?.evaluation_forms;

      if (!form) {
        setResults({ total: 0, analytics: [], sentimentCounts: {}, keywords: [], departmentStats: [], textResponses: [] });
        return;
      }

      // 2. Parallel view queries — sentiment removed (computed client-side from textData)
      const [scaleData, deptRes, textData] = await Promise.all([
        fetchAllRows(() => applyViewFilters(supabase.from('mv_analytics_scale_stats').select('*'))),
        applyViewFilters(supabase.from('mv_analytics_dept_stats').select('*')),
        fetchAllRows(() => applyViewFilters(supabase.from('mv_analytics_text_responses').select('text_response'))),
      ]);

      const deptData = deptRes.data || [];

      // 3. Map scale view rows → question analytics using form field structure
      const analytics = [];
      form.fields.sections.forEach((section, sIdx) => {
        section.fields.forEach((field, fIdx) => {
          if (field.type !== 'SCALE') return;
          const key = `section_${sIdx}_field_${fIdx}`;
          const rows = scaleData.filter(r => r.response_key === key);
          const totalCount   = rows.reduce((n, r) => n + Number(r.response_count || 0), 0);
          const weightedSum  = rows.reduce((n, r) => n + Number(r.avg_value || 0) * Number(r.response_count || 0), 0);
          const avg          = totalCount > 0 ? (weightedSum / totalCount).toFixed(2) : '0';
          const dist         = { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 };
          rows.forEach(r => {
            dist['1'] += Number(r.dist_1 || 0);
            dist['2'] += Number(r.dist_2 || 0);
            dist['3'] += Number(r.dist_3 || 0);
            dist['4'] += Number(r.dist_4 || 0);
            dist['5'] += Number(r.dist_5 || 0);
          });
          analytics.push({ question: field.label, average: avg, count: totalCount, distribution: dist });
        });
      });

      // 4. Keywords + sentiment — computed client-side from text responses
      const textStrings = textData.map(r => r.text_response).filter(Boolean);
      const sentimentCounts = { positive: 0, neutral: 0, negative: 0 };
      textStrings.forEach(t => { sentimentCounts[analyzeSentiment(t)]++; });

      // 5. Department stats aggregation
      const deptMap = {};
      deptData.forEach(r => {
        const dept = r.teacher_dept;
        if (!deptMap[dept]) deptMap[dept] = { count: 0, totalRating: 0 };
        deptMap[dept].count       += Number(r.evaluation_count || 0);
        deptMap[dept].totalRating += Number(r.avg_rating || 0) * Number(r.evaluation_count || 0);
      });
      const departmentStats = Object.entries(deptMap).map(([dept, s]) => ({
        department: dept,
        count:      s.count,
        avgRating:  s.count > 0 ? (s.totalRating / s.count).toFixed(2) : '0',
      }));

      // 6. Keywords — client-side extraction from same textStrings used for sentiment
      const keywords = extractKeywords(textStrings);

      // 7. Total evaluations — derive from dept stats or scale stats
      const total = deptData.reduce((n, r) => n + Number(r.evaluation_count || 0), 0)
                 || scaleData.reduce((n, r) => n + Number(r.response_count || 0), 0);

      setResults({
        total,
        analytics,
        sentimentCounts,
        keywords,
        departmentStats,
        textResponses: textStrings.slice(0, 10),
        // rawData intentionally omitted — exportEvaluations fetches on-demand
      });
    } catch (err) {
      console.error('Error loading analytics:', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="filters" style={{marginBottom: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px'}}>
        <select value={selectedCampus} onChange={e => setSelectedCampus(e.target.value)}>
          <option value="">All Campus</option>
          <option value="Gubat">Gubat</option>
          <option value="Main">Main</option>
        </select>
        <select value={selectedDepartment} onChange={e => setSelectedDepartment(e.target.value)}>
          <option value="">All Departments</option>
          <option value="BED">BED</option>
          <option value="JHS">JHS</option>
          <option value="SHS">SHS</option>
          <option value="HED">HED</option>
        </select>

        <select value={selectedTeacher} onChange={e => setSelectedTeacher(e.target.value)}>
          <option value="">All Teachers</option>
          {teachers.filter(t => !selectedDepartment || t.department === selectedDepartment).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>

        <select value={selectedSubject} onChange={e => setSelectedSubject(e.target.value)}>
          <option value="">All Subjects</option>
          {subjects.filter(s => !selectedDepartment || s.department === selectedDepartment).map(s => <option key={s.id} value={s.id}>{s.code} - {s.name}</option>)}
        </select>

        <select value={selectedAcademicYear} onChange={e => setSelectedAcademicYear(e.target.value)}>
          <option value="">All Academic Years</option>
          <option value="2024-2025">2024-2025</option>
          <option value="2025-2026">2025-2026</option>
          <option value="2023-2024">2023-2024</option>
        </select>

        <select value={selectedSemester} onChange={e => setSelectedSemester(e.target.value)}>
          <option value="">All Semesters/Quarters</option>
          <option value="1st Semester">1st Semester</option>
          <option value="2nd Semester">2nd Semester</option>
          <option value="1st Quarter">1st Quarter</option>
          <option value="2nd Quarter">2nd Quarter</option>
          <option value="3rd Quarter">3rd Quarter</option>
          <option value="4th Quarter">4th Quarter</option>
          <option value="Summer">Summer</option>
        </select>


        {results && results.total > 0 && (
          <>
            <button onClick={exportAnalytics} style={{background: '#059669', color: 'white', padding: '8px 16px', borderRadius: '6px', fontWeight: '500'}}>
              📊 Export Analytics
            </button>
            <button onClick={exportEvaluations} disabled={exportingEvals} style={{background: exportingEvals ? '#9ca3af' : '#7c3aed', color: 'white', padding: '8px 16px', borderRadius: '6px', fontWeight: '500', cursor: exportingEvals ? 'not-allowed' : 'pointer'}}>
              {exportingEvals ? '⏳ Exporting…' : '📋 Export Evaluations'}
            </button>
          </>
        )}
      </div>

      {loading && (
        <div style={{textAlign: 'center', padding: '40px'}}>
          <div style={{display: 'inline-block', width: '40px', height: '40px', border: '4px solid #e5e7eb', borderTopColor: '#1e40af', borderRadius: '50%'}}></div>
          <p style={{marginTop: '10px', color: '#6b7280'}}>Loading analytics...</p>
        </div>
      )}

      {results && (
        <div className="analytics-results">
          <h3 style={{fontSize: '20px', fontWeight: 'bold', marginBottom: '20px'}}>Evaluation Analytics</h3>
          <p style={{marginBottom: '30px', fontSize: '16px'}}>Total Evaluations: <strong>{results.total}</strong></p>

          {/* Sentiment Analysis */}
          {results.sentimentCounts && (
            <div style={{marginBottom: '40px', padding: '20px', background: '#f9fafb', borderRadius: '8px'}}>
              <h4 style={{fontSize: '18px', fontWeight: '600', marginBottom: '15px'}}>Sentiment Analysis</h4>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px'}}>
                <div style={{padding: '15px', background: '#d1fae5', borderRadius: '8px', textAlign: 'center'}}>
                  <div style={{fontSize: '32px', fontWeight: 'bold', color: '#065f46'}}>{results.sentimentCounts.positive}</div>
                  <div style={{color: '#047857', fontWeight: '500'}}>Positive</div>
                </div>
                <div style={{padding: '15px', background: '#fef3c7', borderRadius: '8px', textAlign: 'center'}}>
                  <div style={{fontSize: '32px', fontWeight: 'bold', color: '#92400e'}}>{results.sentimentCounts.neutral}</div>
                  <div style={{color: '#b45309', fontWeight: '500'}}>Neutral</div>
                </div>
                <div style={{padding: '15px', background: '#fee2e2', borderRadius: '8px', textAlign: 'center'}}>
                  <div style={{fontSize: '32px', fontWeight: 'bold', color: '#991b1b'}}>{results.sentimentCounts.negative}</div>
                  <div style={{color: '#dc2626', fontWeight: '500'}}>Negative</div>
                </div>
              </div>
            </div>
          )}

          {/* Keywords Word Cloud */}
          {results.keywords && results.keywords.length > 0 && (
            <div style={{marginBottom: '40px', padding: '20px', background: '#f9fafb', borderRadius: '8px'}}>
              <h4 style={{fontSize: '18px', fontWeight: '600', marginBottom: '15px'}}>Top Keywords from Comments</h4>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', padding: '10px', maxHeight: '200px', overflowY: 'auto'}}>
                {results.keywords.map((keyword, idx) => {
                  const maxVal = results.keywords[0].value;
                  const size = 12 + (keyword.value / maxVal) * 16;
                  const opacity = 0.5 + (keyword.value / maxVal) * 0.5;
                  return (
                    <span
                      key={idx}
                      style={{
                        fontSize: `${size}px`,
                        fontWeight: '600',
                        color: `rgba(30, 64, 175, ${opacity})`,
                        padding: '3px 8px',
                        background: `rgba(219, 234, 254, ${opacity * 0.5})`,
                        borderRadius: '4px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {keyword.text} ({keyword.value})
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Department Statistics */}
          {results.departmentStats && results.departmentStats.length > 0 && (
            <div style={{marginBottom: '40px'}}>
              <h4 style={{fontSize: '18px', fontWeight: '600', marginBottom: '15px'}}>Department Statistics</h4>
              <table>
                <thead>
                  <tr>
                    <th>Department</th>
                    <th>Evaluations</th>
                    <th>Average Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {results.departmentStats.map((stat, idx) => (
                    <tr key={idx}>
                      <td>{stat.department}</td>
                      <td>{stat.count}</td>
                      <td>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontWeight: '600',
                          background: stat.avgRating >= 4 ? '#d1fae5' : stat.avgRating >= 3 ? '#fef3c7' : '#fee2e2',
                          color: stat.avgRating >= 4 ? '#065f46' : stat.avgRating >= 3 ? '#92400e' : '#991b1b'
                        }}>
                          {stat.avgRating} / 5.0
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Scale Questions Analytics */}
          {results.analytics.length > 0 ? (
            <div style={{marginBottom: '40px'}}>
              <h4 style={{fontSize: '18px', fontWeight: '600', marginBottom: '15px'}}>Rating Distribution</h4>
              <table>
                <thead>
                  <tr>
                    <th>Question</th>
                    <th>Average</th>
                    <th>Responses</th>
                    <th>Distribution (1-5)</th>
                  </tr>
                </thead>
                <tbody>
                  {results.analytics.map((item, idx) => (
                    <tr key={idx}>
                      <td style={{maxWidth: '300px'}}>{item.question}</td>
                      <td>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontWeight: '600',
                          background: item.average >= 4 ? '#d1fae5' : item.average >= 3 ? '#fef3c7' : '#fee2e2',
                          color: item.average >= 4 ? '#065f46' : item.average >= 3 ? '#92400e' : '#991b1b'
                        }}>
                          {item.average} / 5.0
                        </span>
                      </td>
                      <td>{item.count}</td>
                      <td>
                        <div style={{display: 'flex', gap: '5px', alignItems: 'center'}}>
                          {[1, 2, 3, 4, 5].map(rating => (
                            <div key={rating} style={{textAlign: 'center', minWidth: '40px'}}>
                              <div style={{fontSize: '10px', color: '#6b7280'}}>{rating}</div>
                              <div style={{
                                height: '30px',
                                width: '100%',
                                background: '#e5e7eb',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                position: 'relative'
                              }}>
                                <div style={{
                                  height: '100%',
                                  width: '100%',
                                  background: '#3b82f6',
                                  transform: `scaleY(${item.distribution[rating] / item.count})`,
                                  transformOrigin: 'bottom',
                                  transition: 'transform 0.3s'
                                }}></div>
                              </div>
                              <div style={{fontSize: '11px', fontWeight: '600', marginTop: '2px'}}>{item.distribution[rating]}</div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No scale questions found in evaluations.</p>
          )}

          {/* Sample Comments */}
          {results.textResponses && results.textResponses.length > 0 && (
            <div style={{marginBottom: '40px', padding: '20px', background: '#f9fafb', borderRadius: '8px'}}>
              <h4 style={{fontSize: '18px', fontWeight: '600', marginBottom: '15px'}}>Sample Comments</h4>
              <div style={{display: 'grid', gap: '10px'}}>
                {results.textResponses.map((comment, idx) => {
                  const sentiment = analyzeSentiment(comment);
                  return (
                    <div key={idx} style={{
                      padding: '12px',
                      background: 'white',
                      borderRadius: '6px',
                      borderLeft: `4px solid ${sentiment === 'positive' ? '#10b981' : sentiment === 'negative' ? '#ef4444' : '#f59e0b'}`
                    }}>
                      <div style={{fontSize: '14px', color: '#374151', marginBottom: '5px'}}>{comment}</div>
                      <div style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: sentiment === 'positive' ? '#10b981' : sentiment === 'negative' ? '#ef4444' : '#f59e0b'
                      }}>
                        {sentiment.toUpperCase()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function OverviewTab({ setActiveTab, setAssignmentView, setAssignmentStatusFilter }) {
  const [stats, setStats] = useState(null);
  const [filterCampus, setFilterCampus] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefreshViews = async () => {
    // Batch 1 only — fast non-JSONB views used by Overview (no sentiment/analytics)
    setRefreshing(true);
    try {
      const { error } = await supabase.rpc('refresh_evaluation_views', { batch_num: 1 });
      if (error) throw error;
      toast.success('Data refreshed!');
      loadStats();
    } catch (err) {
      toast.error('Refresh failed: ' + err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const getLevelOptions = () => {
    switch(filterDepartment) {
      case 'BED': return ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6'];
      case 'JHS': return ['Grade 7', 'Grade 8', 'Grade 9', 'Grade 10'];
      case 'SHS': return ['Grade 11', 'Grade 12'];
      case 'HED': return ['College'];
      default: return [];
    }
  };

  useEffect(() => {
    loadStats();
  }, [filterCampus, filterDepartment, filterLevel]); // eslint-disable-line

  const loadStats = async () => {
    setLoading(true);
    try {
      // Load teachers
      let teachersQuery = supabase.from('teachers').select('*');
      if (filterDepartment) teachersQuery = teachersQuery.eq('department', filterDepartment);
      const { data: teachers } = await teachersQuery;

      // Load subjects
      let subjectsQuery = supabase.from('subjects').select('*');
      if (filterDepartment) subjectsQuery = subjectsQuery.eq('department', filterDepartment);
      const { data: subjects } = await subjectsQuery;

      // Load students
      let studentsQuery = supabase.from('student_accounts').select('*');
      if (filterCampus) studentsQuery = studentsQuery.eq('campus', filterCampus);
      if (filterDepartment) studentsQuery = studentsQuery.eq('department', filterDepartment);
      if (filterLevel) studentsQuery = studentsQuery.eq('level', filterLevel);
      const { data: students } = await studentsQuery;

      // Load assignments (teacher_subjects)
      let assignmentsQuery = supabase.from('teacher_subjects').select('*');
      if (filterDepartment) assignmentsQuery = assignmentsQuery.eq('department', filterDepartment);
      if (filterLevel) assignmentsQuery = assignmentsQuery.eq('level', filterLevel);
      const { data: assignments } = await assignmentsQuery;

      // Load completion stats from materialized views (fast — pre-computed, no full table scan)
      const [completionRes, evalCountRes] = await Promise.all([
        supabase.from('mv_student_completion_stats').select('*'),
        supabase.from('mv_eval_counts_by_campus_dept').select('*'),
      ]);
      const completionData = completionRes.data || [];
      const evalCountData  = evalCountRes.data  || [];

      // Filter the small view datasets client-side
      let filtCompletion = completionData;
      if (filterCampus)     filtCompletion = filtCompletion.filter(r => r.campus      === filterCampus);
      if (filterDepartment) filtCompletion = filtCompletion.filter(r => r.student_dept === filterDepartment);
      if (filterLevel)      filtCompletion = filtCompletion.filter(r => r.level        === filterLevel);

      let filtEvalCount = evalCountData;
      if (filterCampus)     filtEvalCount = filtEvalCount.filter(r => r.campus       === filterCampus);
      if (filterDepartment) filtEvalCount = filtEvalCount.filter(r => r.teacher_dept === filterDepartment);

      const totalStudents     = filtCompletion.reduce((n, r) => n + (r.students_assigned  || 0), 0);
      const studentsCompleted = filtCompletion.reduce((n, r) => n + (r.students_completed || 0), 0);
      const studentsPending   = totalStudents - studentsCompleted;
      const completionRate    = totalStudents > 0 ? ((studentsCompleted / totalStudents) * 100).toFixed(1) : 0;
      const totalEvaluations  = filtEvalCount.reduce((n, r) => n + (r.total_evaluations || 0), 0);

      console.log('Overview Debug:', { totalStudents, studentsCompleted, totalEvaluations, filterDepartment, filterLevel });

      // Group by department
      const teachersByDept = {};
      const subjectsByDept = {};
      const studentsByDept = {};

      (teachers || []).forEach(t => {
        const dept = t.department || 'Unknown';
        teachersByDept[dept] = (teachersByDept[dept] || 0) + 1;
      });

      (subjects || []).forEach(s => {
        const dept = s.department || 'Unknown';
        subjectsByDept[dept] = (subjectsByDept[dept] || 0) + 1;
      });

      (students || []).forEach(s => {
        const dept = s.department || 'Unknown';
        if (!studentsByDept[dept]) {
          studentsByDept[dept] = {};
        }
        const level = s.level || 'Unknown';
        studentsByDept[dept][level] = (studentsByDept[dept][level] || 0) + 1;
      });

      setStats({
        totalTeachers: teachers?.length || 0,
        teachersData: teachers,
        subjectsData: subjects,
        studentsData: students,
        totalSubjects: subjects?.length || 0,
        totalStudents: totalStudents,
        teachersByDept,
        subjectsByDept,
        studentsByDept,
        activeTeachers: teachers?.filter(t => t.is_active).length || 0,
        approvedStudents: students?.filter(s => s.status === 'approved').length || 0,
        pendingStudents: students?.filter(s => s.status === 'pending').length || 0,
        // Evaluation progress metrics
        totalEvaluations: totalEvaluations,
        studentsCompleted: studentsCompleted,
        studentsPending: studentsPending,
        completionRate: completionRate
      });

      console.log("Overview Stats - Teachers:", teachers?.length, "Subjects:", subjects?.length, "Students:", students?.length);
    } catch (err) {
      console.error('Error loading stats:', err);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{textAlign: 'center', padding: '40px'}}>
        <div style={{display: 'inline-block', width: '40px', height: '40px', border: '4px solid #e5e7eb', borderTopColor: '#1e40af', borderRadius: '50%'}}></div>
        <p style={{marginTop: '10px', color: '#6b7280'}}>Loading statistics...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{fontSize: '24px', fontWeight: 'bold', marginBottom: '20px'}}>System Overview</h2>

      {/* Filters */}
      <div style={{marginBottom: '30px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap'}}>
        <select value={filterCampus} onChange={e => setFilterCampus(e.target.value)} style={{padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db'}}>
          <option value="">All Campus</option>
          <option value="Gubat">Gubat</option>
          <option value="Main">Main</option>
        </select>
        <select value={filterDepartment} onChange={e => { setFilterDepartment(e.target.value); setFilterLevel(''); }} style={{padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db'}}>
          <option value="">All Departments</option>
          <option value="BED">BED - Basic Education</option>
          <option value="JHS">JHS - Junior Highschool</option>
          <option value="SHS">SHS - Senior Highschool</option>
          <option value="HED">HED - Higher Education</option>
        </select>

        {filterDepartment && (
          <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)} style={{padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db'}}>
            <option value="">All Levels</option>
            {getLevelOptions().map(lvl => <option key={lvl} value={lvl}>{lvl}</option>)}
          </select>
        )}

        {(filterCampus || filterDepartment || filterLevel) && (
          <button onClick={() => { setFilterCampus(''); setFilterDepartment(''); setFilterLevel(''); }} style={{padding: '8px 16px', background: '#6b7280', color: 'white', borderRadius: '6px', border: 'none', cursor: 'pointer'}}>
            Clear Filters
          </button>
        )}
        <button
          onClick={handleRefreshViews}
          disabled={refreshing}
          title="Refresh pre-computed statistics (run after new submissions)"
          style={{padding: '8px 16px', background: refreshing ? '#9ca3af' : '#1e40af', color: 'white', borderRadius: '6px', border: 'none', cursor: refreshing ? 'not-allowed' : 'pointer', marginLeft: 'auto'}}
        >
          {refreshing ? 'Refreshing…' : '↺ Refresh Data'}
        </button>
      </div>

      {stats && (
        <>
          {/* Evaluation Progress */}
          <div style={{background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '30px'}}>
            <h3 style={{fontSize: '20px', fontWeight: '600', marginBottom: '20px'}}>Evaluation Progress</h3>
            
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px'}}>
              <div style={{background: '#fef3c7', padding: '16px', borderRadius: '8px', textAlign: 'center'}}>
                <div style={{fontSize: '32px', fontWeight: 'bold', color: '#92400e'}}>{stats.totalEvaluations}</div>
                <div style={{fontSize: '14px', color: '#b45309', marginTop: '4px'}}>Total Submissions</div>
              </div>
              
              <div style={{background: '#f3f4f6', padding: '16px', borderRadius: '8px', textAlign: 'center'}}>
                <div style={{fontSize: '32px', fontWeight: 'bold', color: '#1f2937'}}>{stats.totalStudents}</div>
                <div style={{fontSize: '14px', color: '#6b7280', marginTop: '4px'}}>Total Students</div>
              </div>
              
              <div style={{background: '#d1fae5', padding: '16px', borderRadius: '8px', textAlign: 'center'}}>
                <div style={{fontSize: '32px', fontWeight: 'bold', color: '#065f46'}}>{stats.studentsCompleted}</div>
                <div style={{fontSize: '14px', color: '#047857', marginTop: '4px'}}>Students Completed</div>
              </div>
              
              <div style={{background: '#fee2e2', padding: '16px', borderRadius: '8px', textAlign: 'center', cursor: 'pointer'}} onClick={() => {
                setAssignmentView('student-subject');
                setAssignmentStatusFilter('pending');
                setActiveTab('assignments');
              }}>
                <div style={{fontSize: '32px', fontWeight: 'bold', color: '#991b1b'}}>{stats.studentsPending}</div>
                <div style={{fontSize: '14px', color: '#dc2626', marginTop: '4px'}}>Students Pending</div>
              </div>
            </div>

            {/* Completion Rate Progress Bar */}
            <div style={{marginTop: '20px'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px'}}>
                <span style={{fontSize: '14px', fontWeight: '600', color: '#374151'}}>Overall Completion Rate</span>
                <span style={{fontSize: '14px', fontWeight: '700', color: '#1f2937'}}>{stats.completionRate}%</span>
              </div>
              <div style={{width: '100%', height: '24px', background: '#e5e7eb', borderRadius: '12px', overflow: 'hidden'}}>
                <div style={{
                  width: `${stats.completionRate}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                  transition: 'width 0.5s ease'
                }}></div>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '40px'}}>
            <div style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '12px', padding: '24px', color: 'white', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
              <div style={{fontSize: '14px', opacity: 0.9, marginBottom: '8px'}}>Total Teachers</div>
              <div style={{fontSize: '36px', fontWeight: 'bold'}}>{stats.totalTeachers}</div>
              <div style={{fontSize: '12px', opacity: 0.8, marginTop: '8px'}}>{stats.activeTeachers} active</div>
            </div>

            <div style={{background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', borderRadius: '12px', padding: '24px', color: 'white', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
              <div style={{fontSize: '14px', opacity: 0.9, marginBottom: '8px'}}>Total Subjects</div>
              <div style={{fontSize: '36px', fontWeight: 'bold'}}>{stats.totalSubjects}</div>
              <div style={{fontSize: '12px', opacity: 0.8, marginTop: '8px'}}>Across all departments</div>
            </div>

            <div style={{background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', borderRadius: '12px', padding: '24px', color: 'white', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
              <div style={{fontSize: '14px', opacity: 0.9, marginBottom: '8px'}}>Total Students</div>
              <div style={{fontSize: '36px', fontWeight: 'bold'}}>{stats.totalStudents}</div>
              <div style={{fontSize: '12px', opacity: 0.8, marginTop: '8px'}}>{stats.approvedStudents} approved, {stats.pendingStudents} pending</div>
            </div>
          </div>

          {/* Department Breakdown */}
          <div style={{background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '30px'}}>
            <h3 style={{fontSize: '20px', fontWeight: '600', marginBottom: '20px'}}>Department Breakdown</h3>
            
            <table style={{width: '100%', borderCollapse: 'collapse'}}>
              <thead>
                <tr style={{background: '#f9fafb', borderBottom: '2px solid #e5e7eb'}}>
                  <th style={{padding: '12px', textAlign: 'left', fontWeight: '600'}}>Department</th>
                  <th style={{padding: '12px', textAlign: 'center', fontWeight: '600'}}>Teachers</th>
                  <th style={{padding: '12px', textAlign: 'center', fontWeight: '600'}}>Subjects</th>
                  <th style={{padding: '12px', textAlign: 'center', fontWeight: '600'}}>Students</th>
                </tr>
              </thead>
              <tbody>
                {['BED', 'JHS', 'SHS', 'HED'].map(dept => (
                  <tr key={dept} style={{borderBottom: '1px solid #e5e7eb'}}>
                    <td style={{padding: '12px', fontWeight: '500'}}>{dept}</td>
                    <td style={{padding: '12px', textAlign: 'center'}}>{stats.teachersByDept[dept] || 0}</td>
                    <td style={{padding: '12px', textAlign: 'center'}}>{stats.subjectsByDept[dept] || 0}</td>
                    <td style={{padding: '12px', textAlign: 'center'}}>
                      {Object.values(stats.studentsByDept[dept] || {}).reduce((sum, count) => sum + count, 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Students by Level */}
          {!filterLevel && (
            <div style={{background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
              <h3 style={{fontSize: '20px', fontWeight: '600', marginBottom: '20px'}}>Students by Level</h3>
              
              {Object.entries(stats.studentsByDept).map(([dept, levels]) => (
                <div key={dept} style={{marginBottom: '24px'}}>
                  <h4 style={{fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151'}}>{dept}</h4>
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px'}}>
                    {Object.entries(levels).map(([level, count]) => (
                      <div key={level} style={{background: '#f3f4f6', padding: '12px', borderRadius: '8px', textAlign: 'center'}}>
                        <div style={{fontSize: '24px', fontWeight: 'bold', color: '#1f2937'}}>{count}</div>
                        <div style={{fontSize: '12px', color: '#6b7280', marginTop: '4px'}}>{level}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
